import { OnScreenModal } from '@/components/ui/OnScreenModal'
import type { DefaultInfoModalProps, DefaultInfoModalType } from '../types'

export function DefaultInfoModal({ isOpen, onClose, type, status, title }: DefaultInfoModalProps) {
  return (
    <OnScreenModal
      isOpen={isOpen}
      title={title || titleFor(type, status)}
      onClose={onClose}
      maxWidth="max-w-4xl"
    >
      {type === 'default_document' ? (
        <DocumentRequirements status={status} />
      ) : (
        <StatusGuidance status={status} />
      )}
    </OnScreenModal>
  )
}

function titleFor(type: DefaultInfoModalType, status?: string): string {
  const normStatus = (status || '').toUpperCase()
  const statusName = normStatus === 'SKIPPED' ? 'Skipped' : normStatus === 'EVICTION' ? 'Eviction' : ''

  if (type === 'default_document') {
    const base = 'Required Documentation'
    return statusName ? `${base} : ${statusName}` : base
  }

  const base = 'Default Instructions'
  return statusName ? `${base} : ${statusName}` : base
}

function DocumentRequirements({ status }: { status?: string }) {
  const normStatus = (status || '').toUpperCase()
  if (normStatus === 'SKIPPED') {
    return (
      <div className="space-y-4 text-sm text-slate-800">
        <p>
          <strong>Skipped Reimbursement</strong> – This is the reimbursement type to request when the resident voluntarily vacates the unit with or without notice and without paying monthly rent as outlined in the lease agreement.
        </p>
        <div>
          <p className="font-semibold text-slate-900">Requirement 1: Reimbursement Request</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>File a Reimbursement Request and Report First Notice of Loss. A reimbursement request must be opened within the same first calendar month that rent is not paid.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 2: Signed Lease Agreement</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>The LIBERTY RENT Approved Signed Lease Agreement. A copy of the signed original lease term agreement that ties to the approved LIBERTY RENT reimbursement coverage.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 3: Final Ledger and Move Out Statement</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>The Final Ledger and Move Out Statement once the resident is out of the unit that shows a valid move out date and prior resident status.</li>
            <li>The final ledger must include all credit and debit transactions posted to the resident’s account from the time of move in through to the time of move out.</li>
            <li>If the property collects a Security Deposit from the resident, LIBERTY RENT will apply the security deposit to cleaning, damage, and legal charges posted to the ledger, and any remaining unused portion will be deducted from the settlement.</li>
            <li>A preliminary ledger may be requested during the review process before the final ledger is available.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 4: Legal Skip Affidavit</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>The legal Skip Affidavit must be filed within the same first calendar month that rent was not paid. The Skip Affidavit is accessible through the LIBERTY RENT portal on the resident’s reimbursement record, and an authorized property contact is responsible for completing, signing, and electronically filing the Skip Affidavit through the LIBERTY RENT portal.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 5: New Resident Information</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>The New Resident is the resident that moves into the unit once the property has re-leased a unit to a new occupant.</li>
            <li>Once the property regains possession of the unit and secures a new tenant, LIBERTY RENT requires information pertaining to the new resident’s lease start date and move-in date to verify the lost rent reimbursement period end date.
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li><strong>New Resident Lease Start Date</strong> – The property will be required to enter the New Resident Lease Start Date on the Reimbursement Request.</li>
                <li><strong>New Resident Move-In Date</strong> – The property will be required to enter the New Resident Move-In Date on the Reimbursement Request.</li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    )
  }
  if (normStatus === 'EVICTION') {
    return (
      <div className="space-y-4 text-sm text-slate-800">
        <p>
          <strong>Eviction Reimbursement –</strong> This is the reimbursement type to request when the resident is in the unit and is not paying monthly rent as outlined in the lease agreement, and the property must take legal action to regain possession of a unit.
        </p>
        <div>
          <p className="font-semibold text-slate-900">Requirement 1: Reimbursement Request</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>File a Reimbursement Request and Report First Notice of Loss. A reimbursement request must be opened within the same first calendar month that rent is not paid.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 2: Signed Lease Agreement</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>The LIBERTY RENT Approved Signed Lease Agreement. A copy of the signed original lease term agreement that ties to the approved LIBERTY RENT reimbursement coverage.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 3: Final Ledger and Move Out Statement</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>The Final Ledger and Move Out Statement once the resident is out of the unit that shows a valid move out date and prior resident status. The final ledger must include all credit and debit transactions posted to the resident’s account from the time of move in through to the time of move out. If the property collects a Security Deposit from the resident, LIBERTY RENT will apply the security deposit to cleaning, damage, and legal charges posted to the ledger, and any remaining unused portion will be deducted from the settlement. A preliminary ledger may be requested during the review process before the final ledger is available.</li>
            <li>Accepting Partial Payments may reduce LIBERTY RENT eviction reimbursements. In some areas, accepting partial payments require the eviction process to restart. It is the property’s responsibility to follow local eviction laws while collecting partial payments. There will be a reduction to the reimbursement if collecting a partial payment caused the eviction process to restart.
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>If there is a loss in reimbursement due to late filed eviction lawsuit, the partial payment will not be deducted.</li>
                <li>LIBERTY RENT reserves the right to adjust the eligible reimbursement period when partial payments are received and will do so in such a way that yields the maximum possible reimbursement.</li>
              </ul>
            </li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 4: Eviction Lawsuit filed in the court for non-payment of rent.</p>
          <ul className="list-disc pl-5 mt-1 space-y-2">
            <li>The eviction lawsuit must be filed in the court within the same first calendar month that rent was not paid. The eviction lawsuit form name varies by geographical location.</li>
            <li>
              Common names for the eviction lawsuit form are:
              <ol className="list-[lower-roman] pl-5 mt-1 space-y-1">
                <li>Unlawful Detainer</li>
                <li>Application for Ejectment</li>
                <li>Dispossessory Warrant</li>
              </ol>
            </li>
            <li>
              <strong>CARES Act 30 day Notice Eviction Filing Delay Exception:</strong> In the event a property is covered under the CARES Act and is required to issue a 30 day Notice to Vacate/Pay, LIBERTY RENT will also require a copy of the dated notice in order to receive reimbursement for the month of missed rent while waiting for the required period to end before filing for eviction.
            </li>
            <li>
              A copy of an eviction lawsuit is available at the county courthouse and may be available in online public records. It is the property’s responsibility to provide the eviction lawsuit once available from the court.
            </li>
            <li>
              Alternate court issued documents will be accepted for a reduction in reimbursements. If alternate court issued eviction documents are used to fulfill the legal requirement, the reimbursement start date will begin on the date of the alternate court issued eviction document.
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>
                  The most common alternate documents accepted include (yet not limited to) the following:
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Notice of Hearing</li>
                    <li>Judgement</li>
                    <li>Writ of Possession</li>
                  </ul>
                </li>
                <li>
                  Alternate court issued documents not accepted include:
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Eviction filing receipt</li>
                    <li>Online court case summaries</li>
                    <li>Court docket schedules</li>
                    <li>Communication / Eviction filing requests sent to 3<sup>rd</sup> party eviction servicers and/or 3<sup>rd</sup> party legal counsel.</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              Additional legal documents may be requested if the property is unable to file an eviction lawsuit, or if legal events occur while the resident is under eviction.
              <ol className="list-[lower-alpha] pl-5 mt-1 space-y-2">
                <li>
                  <strong>BANKRUPTCY:</strong> Bankruptcy Document – The bankruptcy document is used to verify if the property is identified as an Unsecured Debtor.
                  <ol className="list-decimal pl-5 mt-1 space-y-1">
                    <li>
                      Yes, the property is listed in the bankruptcy = LIBERTY RENT must wait on the bankruptcy hearing to know the status of the outstanding balance at the property. LIBERTY RENT will act in accordance with the bankruptcy hearing.
                      <ol className="list-[lower-alpha] pl-5 mt-1 space-y-1">
                        <li>Debt cleared = no reimbursement</li>
                        <li>Debt remains = reimbursement is issued</li>
                      </ol>
                    </li>
                    <li>
                      No, the property is not listed in the bankruptcy = the reimbursement will be calculated based on the standard requirements.
                    </li>
                  </ol>
                </li>
                <li>
                  <strong>APPEAL:</strong> Court Registry – The court registry is used to account for funds the resident was ordered to pay into a court bond that have not been posted to the ledger. Additional questions regarding the appeal may be asked during the reimbursement review process.
                </li>
                <li>
                  <strong>COURT ORDERED STIPULATION AGREEMENT:</strong> There are three (3) documents that will be requested if there is a court ordered stipulation in place. As a result of an eviction lawsuit, the judgement may also order the resident to make payments into a court bond.
                  <ol className="list-decimal pl-5 mt-1 space-y-1">
                    <li><strong>Stipulation Agreement</strong> – Used to confirm the court ordered a scheduled payment plan over time that paused the eviction process.</li>
                    <li><strong>Default Stipulation</strong> – Filed by the property when a resident does not follow the court ordered Stipulation Agreement, and the property must report the resident’s default. Once the property files the Default Stipulation, the original eviction lawsuit is reinstated.</li>
                    <li><strong>Writ of Ejectment</strong> – Filed by the property once the required time has passed for the resident to respond to the court.</li>
                    <li><strong>Court Registry</strong> – Used to account for funds the resident was ordered to pay into a court bond that have not been posted to the ledger.</li>
                  </ol>
                </li>
              </ol>
            </li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 5: New Resident Information</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>
              The New Resident is the resident that moves into the unit once the property has re-leased a unit to a new occupant. Once the property regains possession of the unit and secures a new tenant, LIBERTY RENT requires information pertaining to the new resident’s lease start date and move-in date to verify the lost rent reimbursement period end date.
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li><strong>New Resident Lease Start Date -</strong> The property will be required to enter the New resident Lease Start Date on the Reimbursement Request.</li>
                <li><strong>New Resident Move-In Date –</strong> The property will be required to enter the New resident Move in Date on the Reimbursement Request.</li>
              </ul>
            </li>
          </ul>
        </div>
        <p className="pt-2 font-medium">
          <strong>NOTE:</strong> If a resident vacates the unit during the eviction process, please read the following guidance. Do not cancel the Reimbursement Request to change the reimbursement type (Skipped or Eviction). Instead, send a message to the Reimbursement Team via the Message Board to request the change.
        </p>
        <p className="font-semibold text-slate-900 pt-2">Skipped During an Eviction Reimbursement –</p>
        <p>
          An Eviction Reimbursement is the reimbursement type used when a resident vacates a unit during the eviction process (most common is during the Notice to Vacate/Pay period before an eviction lawsuit is filed). This is processed as an Eviction Reimbursement and requires LIBERTY RENT enabled access to the legal requirement for the Skipped resident.
        </p>
        <div>
          <p className="font-semibold text-slate-900">Requirement 1: Reimbursement Request</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>File a Reimbursement Request and Report First Notice of Loss. A reimbursement request must be opened within the same first calendar month that rent is not paid.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 2: Signed Lease Agreement</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>The LIBERTY RENT Approved Signed Lease Agreement. A copy of the signed original lease term agreement that ties to the approved LIBERTY RENT reimbursement coverage.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 3: Final Ledger and Move Out Statement</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>The Final Ledger and Move Out Statement once the resident is out of the unit that shows a valid move out date and prior resident status. The final ledger must include all credit and debt transactions posted to the resident’s account from the time of move in through to the time of move out. If the property collects a Security Deposit from the resident, LIBERTY RENT will apply the security deposit to cleaning, damage, and legal charges posted to the ledger, and any remaining unused portion will be deducted from the settlement. A preliminary ledger may be requested during the review process before the final ledger is available.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 4: Notice to Vacate Letter and Skip Affidavit</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>
              The legal requirement for a reimbursement request on a resident that skipped before the eviction lawsuit could be filed in the court include:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>Notice to Vacate letter issued prior to filing the eviction lawsuit for non-payment of rent.</li>
                <li>The Skip Affidavit is provided by LIBERTY RENT and is accessible through the LIBERTY RENT portal on the resident’s reimbursement record. An authorized property contact is responsible for completing, signing, and electronically filing the Skip Affidavit through the LIBERTY RENT portal.</li>
              </ul>
            </li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Requirement 5: New Resident Information</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>
              The New Resident is the resident that moves into the unit once the property has re-leased a unit to a new occupant. Once the property regains possession of the unit and secures a new tenant, LIBERTY RENT requires information pertaining to the new resident’s lease start date and move-in date to verify the lost rent reimbursement period end date.
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>New Resident Lease Start Date – The property will be required to enter the New Resident Lease Start Date on the Reimbursement Request.</li>
                <li>New Resident Move-In Date – The property will be required to enter the New Resident Move in Date on the Reimbursement Request.</li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    )
  }
  return (
    <div className="space-y-3 text-sm text-slate-800">
      <p>
        <strong>1. Lease Agreement:</strong> We only need the first page or two of the Lease Agreement that lists the resident(s), lease dates, rent amount and security deposit amount.
      </p>
      <p>
        <strong>2. Final Payment Ledger:</strong> We need the final payment ledger to show a running cumulative balance of all credits and expenses from the period the rent payment was not made in full until the date the unit was vacated.
      </p>
      <p>
        <strong>3. Notification Date:</strong> is populated automatically when the status is saved as Skipped. We assume the unit is vacant and getting ready for new resident at time of notice.
      </p>
    </div>
  )
}

function StatusGuidance({ status }: { status?: string }) {
  const normStatus = (status || '').toUpperCase()
  return (
    <div className="space-y-4 text-sm text-slate-800">
      {(normStatus === 'NOT_STARTED' || normStatus === 'ACTIVE') && (
        <div className="rounded-md bg-amber-50 p-3 border border-amber-200 text-amber-900">
          <p>
            <strong>Not Started:</strong> In order to provide clarity about the Residents’ status, after the Liberty Rent Certificate is bought, the initial status will be “Not Started” until the Lease Start Date arrives. Then it will automatically update to ‘Active’. Otherwise, let us know if the Resident did not move in and why.
          </p>
        </div>
      )}
      {normStatus === 'CLOSED' && (
        <div className="rounded-md bg-amber-50 p-3 border border-amber-200 text-amber-900">
          <p>
            <strong>Closed:</strong> In order to provide better clarity about the Residents’ status, after the Liberty Rent Certificate is expires, the Rental Status will automatically be “Closed” with a “Normal Lease End” for the progress. Otherwise, let us know if the Resident had an “Early End” by selecting these values.
          </p>
        </div>
      )}
      {normStatus === 'SKIPPED' && (
        <div className="rounded-md bg-amber-50 p-3 border border-amber-200 text-amber-900">
          <p>
            <strong>Skips or Abandonments:</strong> You MUST notify us through this portal that a resident has skipped or abandoned the unit in the month in which it occurs. The Property will be made whole for lost rent from the month reported until the unit is re-let, 6 months, or the lease expires, whichever is shorter. However, if you do not notify us during the month of the event (Skip), OR do not provide the information required then you are accepting responsibility and forfeiting payments for each month the information is not provided. The only exception is regarding the date the unit is re-let, which may take some time. All of the other information should be provided shortly after the status update.
          </p>
        </div>
      )}
      {normStatus === 'EVICTION' && (
        <div className="rounded-md bg-amber-50 p-3 border border-amber-200 text-amber-900">
          <p>
            <strong>Evictions:</strong> You MUST notify us through this portal that a resident is being evicted by uploading a court stamped Unlawful Detainer file in the month in which it occurs. For example, if your resident did not pay in July, then you must upload the UD in July. Otherwise, the community forfeits the rent payment for that month. The Property will be made whole for lost rent from the month the UD is uploaded until the unit is re-let, 6 months, or the lease expires, whichever is shorter. You must provide all of the information identified in the Required Information section shortly after the status update to be made whole. The only exception is regarding the date the unit is re-let, which may take some time.
          </p>
        </div>
      )}

      {/* Rental Status Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200 rounded">
          <thead className="bg-slate-100 text-slate-700 font-semibold border-b">
            <tr>
              <th className="p-2 text-left border-r">Rental Status</th>
              <th className="p-2 text-left border-r">Rental Progress</th>
              <th className="p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <tr>
              <td className="p-2 border-r align-top" rowSpan={2}>Not Started</td>
              <td className="p-2 border-r align-top">Lease Pending</td>
              <td className="p-2">Lease Start Date in future (normal)</td>
            </tr>
            <tr>
              <td className="p-2 border-r align-top">Not Moved In</td>
              <td className="p-2">Certificate bought then Cancelled / Refunded</td>
            </tr>
            <tr>
              <td className="p-2 border-r align-top">Active</td>
              <td className="p-2 border-r align-top">Current Resident</td>
              <td className="p-2">Based on Lease Start Date thru End Date</td>
            </tr>
            <tr>
              <td className="p-2 border-r align-top" rowSpan={4}>Eviction / Skipped</td>
              <td className="p-2 border-r align-top">Info Needed</td>
              <td className="p-2">Documentation and Dates needed – when all are uploaded, then switch to Under Review</td>
            </tr>
            <tr>
              <td className="p-2 border-r align-top">No Response</td>
              <td className="p-2">If property does not provide required info by Lease End date – then property loses right to reimbursement?</td>
            </tr>
            <tr>
              <td className="p-2 border-r align-top">Under LR Review</td>
              <td className="p-2">Liberty Rent Reviewing for Payment / Negotiations with Property.</td>
            </tr>
            <tr>
              <td className="p-2 border-r align-top">Completed</td>
              <td className="p-2">Settlement Payment Made OR manually set by Default Mgr when No Response more than 30 days</td>
            </tr>
            <tr>
              <td className="p-2 border-r align-top" rowSpan={2}>Closed</td>
              <td className="p-2 border-r align-top">Normal End</td>
              <td className="p-2">Normal Completion of Lease</td>
            </tr>
            <tr>
              <td className="p-2 border-r align-top">Early End</td>
              <td className="p-2">Resident Paid Out to leave early (e.g. Job change, moved –no payment from Liberty Rent)</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Reasons Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200 rounded">
          <thead className="bg-slate-100 text-slate-700 font-semibold border-b">
            <tr>
              <th className="p-2 text-left border-r w-1/3">Reasons</th>
              <th className="p-2 text-left">Examples</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <tr>
              <td className="p-2 border-r font-medium">Background Failed</td>
              <td className="p-2">Late response on criminal or other background info</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Roommate</td>
              <td className="p-2">Residents / income separated</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Divorce / Separation</td>
              <td className="p-2">Residents / income separated</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Domestic Abuse / Crime</td>
              <td className="p-2">Income separated, arrested</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Family Emergency</td>
              <td className="p-2">Family issue or emergency</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Injury, Health, Death</td>
              <td className="p-2">Health related</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Job Transfer / Military</td>
              <td className="p-2">Job situation change</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Loss of Job / Income</td>
              <td className="p-2">Job or benefits lost</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Constructive Eviction</td>
              <td className="p-2">Property not maintained</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Force Majeure</td>
              <td className="p-2">Hurricane, other catastrophic situation</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Property Issue / Not Avail</td>
              <td className="p-2">Unit not avail or Tenant did not like available unit</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Wages /Judgements</td>
              <td className="p-2">Garnishments, Assets frozen</td>
            </tr>
            <tr>
              <td className="p-2 border-r font-medium">Other</td>
              <td className="p-2">Unknown</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
